<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useOddsmatcherStore} from "@/stores/OddsmatcherStore";
import OddsmatcherMatchedBetCard from "@/components/oddsmatcher/OddsmatcherMatchedBetCard.vue";
import OddsmatcherAdvancedFilters from "@/components/oddsmatcher/OddsmatcherAdvancedFilters.vue";
import OddsmatcherEwMbCard from "@/components/oddsmatcher/OddsmatcherEwMbCard.vue";
import OddsmatcherExtraPlaceMbCard from "@/components/oddsmatcher/OddsmatcherExtraPlaceMbCard.vue";

const oddsmatcherStore = useOddsmatcherStore();

const props = defineProps({
      matcherType: {
        type: String,  // Define the type of the prop
        default: 'normal'  // Set the default value to 'normal'
      }
    }
)

const currentPage = ref(1); // Track the current page
const resultsPerPage = 50;
const matchedBets = computed(() => {
  const bets = oddsmatcherStore.getFilteredMatchedBets(oddsmatcherStore.filters)
  if (props.matcherType === 'ep') {
    return bets.sort((a, b) => b.implied_odds - a.implied_odds)
  }
  if (freebet.value) {
    return bets.sort((a, b) => b.snrRating - a.snrRating)
  }
  return bets.sort((a, b) => b.srRating - a.srRating);
});

// Calculate paginated bets based on the current page
const paginatedBets = computed(() => {
  const start = (currentPage.value - 1) * resultsPerPage;
  const end = start + resultsPerPage;
  return matchedBets.value.slice(start, end);
});

// Calculate total pages based on the number of matched bets
const totalPages = computed(() => {
  return Math.ceil(matchedBets.value.length / resultsPerPage);
});

// Navigate to the next page
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

// Navigate to the previous page
const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

watch(
    () => oddsmatcherStore.filters,
    () => {
      currentPage.value = 1; // Reset to the first page when filters change
    },
    {deep: true}
);

const freebet = computed(() => {
  return (
      oddsmatcherStore.filters &&
      oddsmatcherStore.filters.betType &&
      oddsmatcherStore.filters.betType === 'freebet'
  );
})

const isLoading = ref(false);

onMounted(async () => {
  await handleRefresh()
});

// Function to handle refresh click
const handleRefresh = async () => {
  isLoading.value = true;  // Start loading animation
  await oddsmatcherStore.fetchAndUpdateData(props.matcherType);  // Simulate fetch data call
  isLoading.value = false;  // Stop loading animation
};


</script>

<template>
  <oddsmatcher-advanced-filters
      :matcher-type="matcherType"
  />
  <!-- Pagination Controls -->
  <div style="display: flex; justify-content: center; gap: 3rem; margin-block: 20px">
    <div class="pagination">
      <button @click="previousPage" :disabled="currentPage === 1">Previous</button>
      <span style="padding: 8px 12px">Page {{ currentPage }} of {{ totalPages }}</span>
      <button @click="nextPage" :disabled="currentPage === totalPages">Next</button>
    </div>

    <div style="display: flex; align-items: center; justify-content: center;">
      <button
          @click="handleRefresh"
          :disabled="isLoading"
          style="background-color: white; color: #0d6efd; border: 2px solid #0d6efd; border-radius: 24px; padding: 10px 20px; display: flex; align-items: center; justify-content: center; gap: 10px; font-size: 16px; cursor: pointer; transition: background-color 0.3s, color 0.3s;">
        <i v-if="!isLoading" class="bi bi-arrow-repeat" style="font-size: 20px;"></i>
        <span v-else class="loading-spinner"></span>
        Reload
      </button>
    </div>


  </div>

  <div v-if="Object.keys(paginatedBets).length > 0">
    <!--  todo no bet.id  -->
    <template v-for="bet in paginatedBets" :key="bet.id">
      <oddsmatcher-ew-mb-card v-if="matcherType === 'ew'" :bet="bet" :freebet="freebet"/>
      <oddsmatcher-extra-place-mb-card v-else-if="matcherType === 'ep'" :bet="bet" :freebet="freebet"/>
      <oddsmatcher-matched-bet-card v-else :bet="bet" :freebet="freebet"/>
    </template>
  </div>

  <div v-else style="display: flex; justify-content: center; font-weight: var(--fw-semi-bold)" class="p-large">
    <div style="max-width: 756px" v-if="!isLoading">
      <template v-if="matcherType === 'bog'">There are no matched bets that meet these requirements. BOG are only
        allowed the day of the race. If there are no races left in the day then there may not be any BOG bets available.
        If this is the case try again tomorrow (the best
        time is a few hours before the race starts). Alternatively, try loosening the filters.
      </template>
      <template v-else-if="matcherType === 'ep' || matcherType === 'ew'">There are no matched bets that meet these
        requirements. We only show horse races the day of the race. If there are no races left in the day then there may
        not be any available bets. If this is the case try again tomorrow. Alternatively, try loosening the filters.
      </template>
      <template v-else>
        There are no matched bets that meet these requirements. Try loosening the filters.
      </template>
    </div>

  </div>


</template>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  margin-block: 20px;
}

button {
  margin: 0 10px;
  padding: 8px 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

button:disabled {
  background-color: lightgray;
  cursor: not-allowed;
}

button:disabled {
  background-color: lightgray;
  color: #fff;
  cursor: not-allowed;
}

.loading-spinner {
  border: 2px solid rgba(0, 123, 255, 0.2); /* Lighter blue border */
  border-top: 2px solid #0d6efd; /* Blue color for the spinning part */
  border-radius: 50%; /* Ensures it's a circle */
  width: 18px; /* Width for the circle */
  height: 18px; /* Height for the circle (same as width) */
  animation: spin 1s linear infinite; /* Spinning animation */
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

</style>
